/**
 * customer-registration-api.js
 *
 * Thin API client for the real Customer Registration submission backend.
 * Kept separate from customer-registration.js so backend communication
 * (FormData construction, fetch, response parsing) is isolated from the
 * presentation/form logic.
 *
 * Endpoint contract:
 *   POST {apiBaseUrl}/api/customer-registration   (multipart/form-data)
 *
 *   201 Created  -> CreateCustomerRegistrationResponse
 *   400 Bad Request -> { message: string, errors?: object }
 *   409 Conflict -> PendingRegistrationConflictResponse
 *   other/network -> generic failure
 *
 * IMPORTANT:
 *   - Never set Content-Type manually for multipart/form-data requests;
 *     the browser must generate the boundary.
 *   - Never log the FormData body, the full form object, or einTaxId.
 */

window.CustomerRegistrationAPI = (function () {
  'use strict';

  var ENDPOINT_PATH = '/api/customer-registration';

  /**
   * @typedef {Object} CustomerRegistrationFormInput
   * @property {string} businessType - "Barber" by default (when ownsOrRepresentsBusiness is false),
   *   otherwise the selected Business Type ("barbershop" | "wholesaler").
   * @property {string} firstName
   * @property {string} lastName
   * @property {string} phone
   * @property {string} email
   * @property {boolean} ownsOrRepresentsBusiness
   * @property {string} [businessName]
   * @property {string} [einTaxId]
   * @property {string} [socialMedia]
   * @property {string} [streetAddress1]
   * @property {string} [streetAddress2]
   * @property {string} [city]
   * @property {string} [stateProvinceRegion]
   * @property {string} [postalCode]
   * @property {string} [country]
   * @property {string} [priceLibraryId] - P21 price library ID, if selected via business search.
   * @property {string} [barberCount] - Barbershop / Salon Owner only.
   * @property {string} ["true"|"false"] [currentlyRetailsProducts] - Barbershop / Salon Owner only.
   * @property {string} [whereShopNow] - Only when currentlyRetailsProducts === "true".
   * @property {string} ["true"|"false"] [interestedInSellingProducts] - Only when currentlyRetailsProducts === "false".
   * @property {string} [shopFrequency] - e.g. "Weekly", "Monthly". Barbershop / Salon Owner only.
   * @property {string} [shopArrangement] - e.g. "Booth Rental", "Commission". Barbershop / Salon Owner only.
   *
   * @typedef {Object} CreateCustomerRegistrationResponse
   * @property {number} registrationId
   * @property {string} email
   * @property {"Pending"} registrationStatus
   * @property {number} verificationDocumentCount
   * @property {string} message
   *
   * @typedef {Object} PendingRegistrationConflictResponse
   * @property {string} message
   * @property {number} registrationId
   */

  // Removes a trailing slash so `${base}${path}` never produces "//".
  function normalizeBaseUrl(base) {
    return (base || '').trim().replace(/\/+$/, '');
  }

  function buildFormData(form, verificationDocuments) {
    var formData = new FormData();

    formData.append('businessType', form.businessType || '');
    formData.append('firstName', form.firstName || '');
    formData.append('lastName', form.lastName || '');
    formData.append('phone', form.phone || '');
    formData.append('email', form.email || '');
    formData.append('ownsOrRepresentsBusiness', String(!!form.ownsOrRepresentsBusiness));

    if (form.businessName) {
      formData.append('businessName', form.businessName);
    }
    if (form.einTaxId) {
      formData.append('einTaxId', form.einTaxId);
    }
    if (form.socialMedia) {
      formData.append('socialMedia', form.socialMedia);
    }

    formData.append('streetAddress1', form.streetAddress1 || '');
    formData.append('streetAddress2', form.streetAddress2 || '');
    formData.append('city', form.city || '');
    formData.append('stateProvinceRegion', form.stateProvinceRegion || '');
    formData.append('postalCode', form.postalCode || '');
    formData.append('country', form.country || 'United States');

    if (form.priceLibraryId) {
      formData.append('priceLibraryId', form.priceLibraryId);
    }

    // Barbershop / Salon Owner-specific fields.
    if (form.barberCount) {
      formData.append('barberCount', form.barberCount);
    }
    if (form.currentlyRetailsProducts) {
      formData.append('currentlyRetailsProducts', form.currentlyRetailsProducts);
    }
    if (form.whereShopNow) {
      formData.append('whereShopNow', form.whereShopNow);
    }
    if (form.interestedInSellingProducts) {
      formData.append('interestedInSellingProducts', form.interestedInSellingProducts);
    }
    if (form.shopFrequency) {
      formData.append('shopFrequency', form.shopFrequency);
    }
    if (form.shopArrangement) {
      formData.append('shopArrangement', form.shopArrangement);
    }

    (verificationDocuments || []).forEach(function (file) {
      formData.append('verificationDocuments', file);
    });

    return formData;
  }

  /**
   * Submits the Customer Registration form to the real backend.
   *
   * @param {string} apiBaseUrl
   * @param {CustomerRegistrationFormInput} form
   * @param {File[]} verificationDocuments
   * @returns {Promise<
   *   { status: 'success', data: CreateCustomerRegistrationResponse } |
   *   { status: 'conflict', data: PendingRegistrationConflictResponse } |
   *   { status: 'validationError', message: string, errors: (object|undefined) } |
   *   { status: 'networkError', message: string }
   * >}
   */
  function submitCustomerRegistration(apiBaseUrl, form, verificationDocuments) {
    var url = normalizeBaseUrl(apiBaseUrl) + ENDPOINT_PATH;
    var formData = buildFormData(form, verificationDocuments);

    return fetch(url, {
      method: 'POST',
      // Do NOT set Content-Type — the browser must generate the
      // multipart boundary for FormData bodies.
      body: formData
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var data = null;
          if (text) {
            try { data = JSON.parse(text); } catch (e) { data = null; }
          }
          return { httpStatus: response.status, data: data };
        });
      })
      .then(function (result) {
        if (result.httpStatus === 201) {
          return { status: 'success', data: result.data };
        }
        if (result.httpStatus === 409) {
          return { status: 'conflict', data: result.data };
        }
        if (result.httpStatus === 400) {
          return {
            status: 'validationError',
            message: (result.data && result.data.message) ||
              'Please review the highlighted fields and try again.',
            errors: result.data && result.data.errors
          };
        }
        return {
          status: 'networkError',
          message: 'We couldn\'t submit your application right now. Please try again.'
        };
      })
      .catch(function () {
        return {
          status: 'networkError',
          message: 'We couldn\'t submit your application right now. Please try again.'
        };
      });
  }

  return {
    submitCustomerRegistration: submitCustomerRegistration
  };
})();
